import { useState } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useMutation } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/getErrorMessage";
import type { Trade } from "@/interfaces/trade";
import { queryClient } from "@/services/queryClient";
import { useAppSelector } from "@/store/hooks";
import { useNavigate } from "react-router-dom";

interface LeaveReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  trade: Trade | null;
}

export default function LeaveReviewDialog({
  open,
  onOpenChange,
  onSuccess,
  trade,
}: LeaveReviewDialogProps) {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const navigate = useNavigate();

  const [comment, setComment] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [type, setType] = useState<"POSITIVE" | "NEGATIVE">("POSITIVE");

  const createReview = async (data: any) => {
    const res = await makeRequest.post("/review", data);

    return res.data;
  };

  const mutation = useMutation({
    mutationFn: createReview,
    onSuccess: (result) => {
      toast.success("Review submitted successfully");

      queryClient.invalidateQueries({
        queryKey: ["trades", userId],
      });

      queryClient.invalidateQueries({
        queryKey: ["profile", userId],
      });

      queryClient.invalidateQueries({
        queryKey: ["user-score", userId],
      });

      queryClient.invalidateQueries({
        queryKey: ["user-score", result?.targetUser?.id],
      });

      queryClient.invalidateQueries({
        queryKey: ["reviews-merchant", result?.targetUser?.id],
      });

      navigate(`/p2p/merchant/${result.targetUser.id}?tab=reviews`);
    },

    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to submit review");
      console.error("Error submitting review:", error);
    },
  });

  const submit = async () => {
    if (!comment.trim()) return;

    const res = await mutation.mutateAsync({
      tradeId: trade?.id,
      type,
      text: comment,
      isAnonymous: anonymous,
    });

    setComment("");
    setAnonymous(false);
    onSuccess?.();
    onOpenChange(false);
    setType("POSITIVE");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-3xl">
        <DialogHeader>
          <DialogTitle>Leave Review</DialogTitle>

          <DialogDescription>
            Share your trading experience with this merchant.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <Label className="mb-1">Review Type</Label>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setType("POSITIVE")}
                className={`rounded-xl border p-4 text-left transition-all ${
                  type === "POSITIVE"
                    ? "border-green-500 bg-green-50 dark:bg-green-950/20"
                    : "border-border hover:border-green-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span className="font-medium">Positive</span>
                </div>

                <p className="mt-2 text-xs text-muted-foreground">
                  The trader was trustworthy, responsive and the trade completed
                  successfully.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setType("NEGATIVE")}
                className={`rounded-xl border p-4 text-left transition-all ${
                  type === "NEGATIVE"
                    ? "border-red-500 bg-red-50 dark:bg-red-950/20"
                    : "border-border hover:border-red-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span className="font-medium">Negative</span>
                </div>

                <p className="mt-2 text-xs text-muted-foreground">
                  The trader caused delays, communication issues or other
                  problems during the trade.
                </p>
              </button>
            </div>
          </div>

          <div>
            <Label className="mb-1">Comment</Label>
            <Textarea
              rows={5}
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell other traders about your experience..."
            />

            <div className="mt-2 text-right text-xs text-muted-foreground">
              {comment.length}/500
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Checkbox
              checked={anonymous}
              onCheckedChange={(v) => setAnonymous(Boolean(v))}
            />
            <Label>Submit anonymously</Label>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit Review
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
